/**
 * TeamPage - Public-facing team page
 * Displays team info, records, schedule, and results
 * Supports multiple division entries with tabs
 */
import { useState, useEffect } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trophy, Users, Calendar, MapPin,
  ChevronRight, Loader2, Home, Plane, TrendingUp, TrendingDown, ArrowLeft
} from "lucide-react";
import axios from "axios";
import { MetaTags } from "./MetaTags";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const TeamPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDivisionEntry, setActiveDivisionEntry] = useState(null);

  // Get referrer info from location state
  const referrer = location.state?.from;
  const referrerTab = location.state?.tab;
  const referrerLeagueSlug = location.state?.leagueSlug;
  const referrerLeagueName = location.state?.leagueName;

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/leagues/team/${slug}`);
        setTeamData(res.data);
        
        // Set default to "all" if multiple division entries exist
        if (res.data.division_entries?.length > 1) {
          setActiveDivisionEntry("all");
        } else if (res.data.division_entries?.length === 1) {
          setActiveDivisionEntry(res.data.division_entries[0].id);
        }
      } catch (err) {
        console.error("Failed to load team:", err);
        setError("Team not found");
      } finally {
        setLoading(false);
      }
    };
    fetchTeamData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !teamData) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4">
        <Users className="w-16 h-16 text-zinc-700 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Team Not Found</h1>
        <p className="text-zinc-400 mb-6">The team you are looking for does not exist.</p>
        <Link to="/" className="text-orange-500 hover:text-orange-400">
          ← Back to Home
        </Link>
      </div>
    );
  }

  const { team, league, conference, division_entries, standings, upcoming_games, past_games, all_games } = teamData;
  
  // Get current standings based on active division entry
  const getCurrentStandings = () => {
    if (activeDivisionEntry) {
      const entryStanding = standings.find(s => s.division_entry_id === activeDivisionEntry);
      if (entryStanding) return entryStanding.standing;
    }
    // Fall back to main standings
    const mainStanding = standings.find(s => s.type === "main");
    return mainStanding?.standing || {};
  };

  const currentStandings = getCurrentStandings();
  
  // Filter games by division entry if active (but show all if "all" is selected or no entry filtering needed)
  const getFilteredGames = (games) => {
    // If no division entries, show all games
    if (!division_entries || division_entries.length === 0) return games;
    
    // If "all" selected (null), show all games
    if (!activeDivisionEntry || activeDivisionEntry === "all") return games;
    
    // Filter by specific division entry
    return games.filter(g => 
      g.home_division_entry_id === activeDivisionEntry || 
      g.away_division_entry_id === activeDivisionEntry ||
      // Also show games without division entry (general team games)
      (!g.home_division_entry_id && !g.away_division_entry_id)
    );
  };

  const filteredUpcoming = getFilteredGames(upcoming_games);
  const filteredPast = getFilteredGames(past_games);
  
  // Combine all games and sort by date (ascending)
  const allFilteredGames = [...filteredUpcoming, ...filteredPast].sort((a, b) => {
    const dateA = new Date(a.scheduled_date + 'T' + (a.scheduled_time || '00:00'));
    const dateB = new Date(b.scheduled_date + 'T' + (b.scheduled_time || '00:00'));
    return dateA - dateB;
  });

  // Calculate win percentage
  const wins = currentStandings.wins || 0;
  const losses = currentStandings.losses || 0;
  const winPct = wins + losses > 0 ? (wins / (wins + losses)).toFixed(3) : ".000";

  // Back button info - use referrer state if available, otherwise use league from team data
  const backToLeague = referrer ? {
    slug: referrerLeagueSlug,
    name: referrerLeagueName,
    tab: referrerTab
  } : (league ? {
    slug: league.slug,
    name: league.name,
    tab: null
  } : null);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="team-page">
      <MetaTags
        title={`${team.name} | ${league?.name || "Team"} Schedule`}
        description={`View the ${team.name} schedule, results, and standings`}
        image={team.logo_url}
      />

      {/* Back Button */}
      {backToLeague && (
        <div className="bg-zinc-900/50 border-b border-zinc-800">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <button
              onClick={() => navigate(`/league/${backToLeague.slug}`, { 
                state: backToLeague.tab ? { returnToTab: backToLeague.tab } : undefined
              })}
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="back-to-league-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {backToLeague.name}{backToLeague.tab ? ` (${backToLeague.tab.charAt(0).toUpperCase() + backToLeague.tab.slice(1)})` : ""}
            </button>
          </div>
        </div>
      )}

      {/* Team Header */}
      <div 
        className="relative py-12 border-b border-zinc-800"
        style={{ 
          background: `linear-gradient(to bottom, ${team.color || "#333"}30, transparent)` 
        }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {team.logo_url ? (
              <img 
                src={team.logo_url} 
                alt={team.name} 
                className="w-28 h-28 md:w-36 md:h-36 object-contain"
              />
            ) : (
              <div 
                className="w-28 h-28 md:w-36 md:h-36 rounded-2xl flex items-center justify-center text-4xl font-bold text-white"
                style={{ backgroundColor: team.color || "#333" }}
              >
                {team.abbreviation || team.name?.charAt(0)}
              </div>
            )}
            <div className="text-center md:text-left">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase text-white tracking-wider">
                {team.name}
              </h1>
              {team.mascot && (
                <p className="text-lg text-zinc-400 mt-1">{team.mascot}</p>
              )}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-3 text-sm text-zinc-500">
                {team.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {team.city}
                  </span>
                )}
                {conference && (
                  <span>{conference.name}</span>
                )}
                {league && (
                  <Link to={`/league/${league.slug}`} className="text-orange-400 hover:text-orange-300">
                    {league.name}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Division Entry Tabs (if multiple) */}
      {division_entries.length > 1 && (
        <div className="bg-black/50 border-b border-zinc-800">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex overflow-x-auto py-2 gap-2">
              <button
                onClick={() => setActiveDivisionEntry("all")}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeDivisionEntry === "all"
                    ? "bg-orange-500 text-white"
                    : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                }`}
              >
                All Teams
              </button>
              {division_entries.map(entry => (
                <button
                  key={entry.id}
                  onClick={() => setActiveDivisionEntry(entry.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    activeDivisionEntry === entry.id
                      ? "bg-orange-500 text-white"
                      : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                  }`}
                >
                  {entry.display_name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-6">
            <StatBox 
              label="Overall" 
              value={`${wins}-${losses}`} 
              subValue={winPct}
            />
            <StatBox 
              label="Home" 
              value={`${currentStandings.home_wins || 0}-${currentStandings.home_losses || 0}`}
              icon={<Home className="w-4 h-4" />}
            />
            <StatBox 
              label="Away" 
              value={`${currentStandings.away_wins || 0}-${currentStandings.away_losses || 0}`}
              icon={<Plane className="w-4 h-4" />}
            />
            <StatBox 
              label="Conference" 
              value={`${currentStandings.conference_wins || 0}-${currentStandings.conference_losses || 0}`}
            />
            <StatBox 
              label="Division" 
              value={`${currentStandings.division_wins || 0}-${currentStandings.division_losses || 0}`}
              subValue={currentStandings.division_rank ? `(${currentStandings.division_rank})` : null}
            />
            <StatBox 
              label="Streak" 
              value={currentStandings.streak || "-"}
              icon={currentStandings.streak?.startsWith("W") ? 
                <TrendingUp className="w-4 h-4 text-green-400" /> : 
                currentStandings.streak?.startsWith("L") ?
                <TrendingDown className="w-4 h-4 text-red-400" /> : null
              }
              valueColor={currentStandings.streak?.startsWith("W") ? "text-green-400" : 
                         currentStandings.streak?.startsWith("L") ? "text-red-400" : "text-white"}
            />
          </div>
        </div>
      </div>

      {/* Main Content - Single Schedule with all games */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <ScheduleSection 
          games={allFilteredGames} 
          team={team}
          teams={league?.teams || []}
          title="Schedule & Results"
          emptyMessage="No games scheduled"
        />
      </div>
    </div>
  );
};

// Stat Box Component
const StatBox = ({ label, value, subValue, icon, valueColor = "text-white" }) => (
  <div className="text-center">
    <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-2xl font-bold ${valueColor} flex items-center justify-center gap-2`}>
      {icon}
      {value}
    </div>
    {subValue && (
      <div className="text-sm text-zinc-400">{subValue}</div>
    )}
  </div>
);

// Schedule Section - Combined schedule and results
const ScheduleSection = ({ games, team, teams = [], title, emptyMessage }) => {
  // Group games by month
  const gamesByMonth = games.reduce((acc, game) => {
    const date = new Date(game.scheduled_date + 'T12:00:00');
    const monthKey = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(game);
    return acc;
  }, {});
  
  // Get team slug for linking
  const getTeamSlug = (teamId, teamName) => {
    const foundTeam = teams.find(t => t.id === teamId);
    return foundTeam?.slug || teamName?.toLowerCase().replace(/\s+/g, '-');
  };

  if (games.length === 0) {
    return (
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-12 text-center">
        <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <p className="text-zinc-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Calendar className="w-5 h-5 text-orange-500" />
        {title}
      </h2>
      {Object.entries(gamesByMonth).map(([month, monthGames]) => (
        <div key={month}>
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            {month}
          </h3>
          <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-zinc-500 uppercase border-b border-zinc-800">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Opponent</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Location</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Watch</th>
                  <th className="px-4 py-3 font-medium text-right">Time/Result</th>
                </tr>
              </thead>
              <tbody>
                {monthGames.map(game => {
                  const isHome = game.home_team_id === team.id;
                  const opponent = isHome ? game.away_team : game.home_team;
                  const opponentId = isHome ? game.away_team_id : game.home_team_id;
                  const opponentLogo = isHome ? game.away_team_logo : game.home_team_logo;
                  const opponentSlug = getTeamSlug(opponentId, opponent);
                  
                  // Determine result for this team
                  let result = null;
                  let resultColor = "text-zinc-400";
                  const isCompleted = game.is_completed && game.home_score !== null && game.away_score !== null;
                  
                  if (isCompleted) {
                    const teamScore = isHome ? game.home_score : game.away_score;
                    const oppScore = isHome ? game.away_score : game.home_score;
                    const won = teamScore > oppScore;
                    result = `${won ? "W" : "L"} ${teamScore}-${oppScore}`;
                    resultColor = won ? "text-green-400" : "text-red-400";
                  }
                  
                  const gameDate = new Date(game.scheduled_date + 'T12:00:00');
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const gameDay = new Date(game.scheduled_date + 'T12:00:00');
                  gameDay.setHours(0, 0, 0, 0);
                  const isPast = gameDay < today;
                  
                  // Check if there's a broadcast linked
                  const hasBroadcast = game.youtube_url || game.recording_id || game.broadcast_id;
                  const watchUrl = game.recording_id 
                    ? `/on-demand/${game.recording_id}` 
                    : game.youtube_url || null;
                  
                  return (
                    <tr 
                      key={game.id} 
                      className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${
                        isPast && !isCompleted ? "opacity-60" : ""
                      }`}
                    >
                      <td className="px-4 py-4">
                        <div className="text-white font-medium">
                          {gameDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-zinc-500 w-8">
                            {isHome ? "vs" : "@"}
                          </span>
                          {opponentLogo ? (
                            <img src={opponentLogo} alt="" className="w-8 h-8 object-contain" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-zinc-700 flex items-center justify-center text-xs font-bold text-white">
                              {opponent?.charAt(0) || "?"}
                            </div>
                          )}
                          <Link 
                            to={`/team/${opponentSlug}`}
                            className="text-white font-medium hover:text-orange-400 transition-colors"
                          >
                            {opponent}
                          </Link>
                          {game.is_conference_game && (
                            <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                              CONF
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <div className="text-zinc-400 text-sm flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {game.location || (isHome ? "Home" : "Away")}
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        {/* Watch button if broadcast linked, otherwise blank */}
                        {hasBroadcast && watchUrl ? (
                          <Link
                            to={watchUrl}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded transition-colors"
                          >
                            Watch
                          </Link>
                        ) : null}
                      </td>
                      <td className="px-4 py-4 text-right">
                        {isCompleted ? (
                          <span className={`font-bold ${resultColor}`}>{result}</span>
                        ) : game.is_live ? (
                          <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded animate-pulse">
                            LIVE
                          </span>
                        ) : (
                          <span className="text-white">{game.scheduled_time || "TBD"}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TeamPage;
